import React, { useState } from 'react';
import { Modal, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { OrderDetailDto, ApprovalHistoryDto, ApprovalHistoryLineItemDto } from '../../types';
import { formatDateTime } from '../../utils';

interface ApprovalFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderDetail: OrderDetailDto;
}

export function ApprovalFlowModal({ isOpen, onClose, orderDetail }: ApprovalFlowModalProps) {
  const [expandedLevels, setExpandedLevels] = useState<Record<number, boolean>>({});
  const [expandedApprovals, setExpandedApprovals] = useState<Record<string, boolean>>({});

  const toggleLevel = (level: number) => {
    setExpandedLevels(prev => ({ ...prev, [level]: !prev[level] }));
  };

  const toggleApproval = (approvalId: string) => {
    setExpandedApprovals(prev => ({ ...prev, [approvalId]: !prev[approvalId] }));
  };

  const getApprovalIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'approve':
        return <i className="fa-solid fa-check text-green-600 dark:text-green-400"></i>;
      case 'reject':
      case 'decline':
        return <i className="fa-solid fa-xmark text-red-600 dark:text-red-400"></i>;
      case 'pending':
        return <i className="fa-solid fa-clock text-blue-600 dark:text-blue-400"></i>;
      case 'timeout':
        return <i className="fa-solid fa-hourglass-end text-amber-600 dark:text-amber-400"></i>;
      case 'bypassed':
        return <i className="fa-solid fa-forward text-gray-500 dark:text-secondary-400"></i>;
      case 'notified':
        return <i className="fa-solid fa-bell text-blue-600 dark:text-blue-400"></i>;
      default:
        return <i className="fa-solid fa-circle-dot text-gray-600 dark:text-secondary-400"></i>;
    }
  };

  const getActionBadgeVariant = (action: string) => {
    switch (action.toLowerCase()) {
      case 'approve':
        return 'success';
      case 'reject':
      case 'decline':
        return 'error';
      case 'pending':
        return 'warning';
      case 'timeout':
        return 'warning';
      case 'bypassed':
        return 'default';
      case 'notified':
        return 'info';
      default:
        return 'default';
    }
  };

  const getDecisionBadgeVariant = (decision: string) => {
    switch (decision.toLowerCase()) {
      case 'approve':
        return 'success';
      case 'reject':
        return 'error';
      case 'modify':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatResponseTime = (responseTime?: string) => {
    if (!responseTime) return 'N/A';
    
    // Parse duration string (e.g., "01:23:45" or "1.02:30:15")
    const parts = responseTime.split(':');
    if (parts.length < 2) return responseTime;
    
    const days = responseTime.includes('.') ? parseInt(responseTime.split('.')[0]) : 0;
    const hours = parseInt(parts[parts.length - 3] || '0');
    const minutes = parseInt(parts[parts.length - 2]);
    const seconds = parseInt(parts[parts.length - 1]);
    
    const totalHours = days * 24 + hours;
    
    if (totalHours > 0) {
      return `${totalHours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Calculate statistics per level
  const getLevelStatistics = (level: number) => {
    const levelApprovals = orderDetail.approvalHistory.filter(
      approval => approval.level === level
    );

    let totalApprovals = 0;
    let totalRejections = 0;
    let totalModifications = 0;
    let totalBypassed = 0;

    levelApprovals.forEach(approval => {
      // Count bypassed approvers
      if (approval.action.toLowerCase() === 'bypassed') {
        totalBypassed++;
      }
      
      if (approval.lineItemActions) {
        approval.lineItemActions.forEach(action => {
          if (action.decision === 'approve') {
            if (action.approvedQuantity != null && action.approvedQuantity !== action.originalQuantity) {
              totalModifications++;
            } else {
              totalApprovals++;
            }
          } else if (action.decision === 'reject') {
            totalRejections++;
          }
        });
      }
    });

    return { totalApprovals, totalRejections, totalModifications, totalBypassed };
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Approval Flow Details" size="xl">
      <div className="space-y-6">
        {/* Order Summary */}
        <div className="bg-gray-50 dark:bg-secondary-900 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 dark:text-secondary-400 font-medium">Order Number</p>
              <p className="text-sm font-mono">{orderDetail.orderNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-secondary-400 font-medium">Status</p>
              <Badge variant={orderDetail.status === 'Approved' ? 'success' : 'warning'}>
                {orderDetail.status}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-secondary-400 font-medium">Current Level</p>
              <p className="text-sm">
                {orderDetail.currentStep} of {orderDetail.totalSteps}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-secondary-400 font-medium">Pending Approvers</p>
              <p className="text-sm">{orderDetail.pendingApprovers.length}</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Steps</h3>
          
          {Array.from({ length: orderDetail.totalSteps }, (_, index) => {
            const level = index + 1;
            const levelApprovals = orderDetail.approvalHistory.filter(
              approval => approval.level === level
            );
            const levelName = orderDetail.stepNames?.[level] || `Level ${level}`;
            const isExpanded = expandedLevels[level] !== false; // Default to expanded
            const stats = getLevelStatistics(level);

            // Check if this level has any pending approvals
            const hasPendingApprovals = levelApprovals.some(
              approval => approval.status === 'pending'
            );
            const isTerminalOrder = orderDetail.status === 'Cancelled' || orderDetail.status === 'Rejected';
            // Detect a timed-out step: the workflow is done with a timeout and this is
            // the level where the clock ran out (approvals come back as "timeout" status).
            const isTimedOutLevel = level === orderDetail.currentStep &&
              levelApprovals.some(approval => approval.status === 'timeout');
            const isPendingLevel = !isTimedOutLevel && !isTerminalOrder && level === orderDetail.currentStep && hasPendingApprovals;
            const isCompletedLevel = !isTimedOutLevel && (
              level < orderDetail.currentStep ||
              (level === orderDetail.currentStep && !hasPendingApprovals)
            );
            const isBypassedLevel = isCompletedLevel && levelApprovals.some(
              a => a.action?.toLowerCase() === 'bypassed' && a.approverName === 'System'
            );
            const isDeclinedLevel = isCompletedLevel && !isBypassedLevel && levelApprovals.some(
              a => a.action?.toLowerCase() === 'decline' || a.action?.toLowerCase() === 'reject'
            );

            return (
              <div 
                key={level} 
                className={`border rounded-lg ${
                  isTimedOutLevel ? 'border-amber-400 bg-amber-50 dark:bg-amber-950' :
                  isPendingLevel ? 'border-blue-300 bg-blue-50 dark:bg-blue-950' : 
                  isBypassedLevel ? 'border-gray-300 dark:border-secondary-600 bg-gray-50 dark:bg-secondary-900' :
                  isDeclinedLevel ? 'border-red-300 bg-red-50 dark:bg-red-950' :
                  isCompletedLevel ? 'border-green-300 bg-green-50 dark:bg-green-950' : 
                  'border-gray-300 dark:border-secondary-600'
                }`}
              >
                {/* Level Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-opacity-80"
                  onClick={() => toggleLevel(level)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isTimedOutLevel ? 'bg-amber-200' :
                      isPendingLevel ? 'bg-blue-200' :
                      isBypassedLevel ? 'bg-gray-200 dark:bg-secondary-700' :                      isDeclinedLevel ? 'bg-red-200' :                      isCompletedLevel ? 'bg-green-200' :
                      'bg-gray-200 dark:bg-secondary-700'
                    }`}>
                      {isTimedOutLevel ? (
                        <i className="fa-solid fa-hourglass-end text-amber-700 dark:text-amber-400"></i>
                      ) : isPendingLevel ? (
                        <i className="fa-solid fa-clock text-blue-700"></i>
                      ) : isBypassedLevel ? (
                        <i className="fa-solid fa-forward text-gray-500 dark:text-secondary-400"></i>
                      ) : isDeclinedLevel ? (
                        <i className="fa-solid fa-xmark text-red-700 dark:text-red-400"></i>
                      ) : isCompletedLevel ? (
                        <i className="fa-solid fa-check text-green-700 dark:text-green-400"></i>
                      ) : (
                        <span className="text-sm font-semibold text-gray-700 dark:text-secondary-200">{level}</span>
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">{levelName}</h4>
                      <p className="text-xs text-gray-600 dark:text-secondary-400">
                        {isBypassedLevel ? 'Bypassed — no approver assigned' : (
                          <>
                            {levelApprovals.length} {levelApprovals.length === 1 ? 'approver' : 'approvers'}
                            {isTimedOutLevel && ' (Timed Out)'}
                            {isPendingLevel && ' (Pending)'}
                            {isDeclinedLevel && ' (Declined)'}
                            {isCompletedLevel && !isDeclinedLevel && ' (Completed)'}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {/* Statistics */}
                    {(stats.totalApprovals > 0 || stats.totalRejections > 0 || stats.totalModifications > 0 || stats.totalBypassed > 0) && (
                      <div className="flex items-center space-x-2 text-xs">
                        {stats.totalApprovals > 0 && (
                          <span className="flex items-center text-green-700 dark:text-green-400">
                            <i className="fa-solid fa-check mr-1"></i>
                            {stats.totalApprovals}
                          </span>
                        )}
                        {stats.totalModifications > 0 && (
                          <span className="flex items-center text-blue-700">
                            <i className="fa-solid fa-pen-to-square mr-1"></i>
                            {stats.totalModifications}
                          </span>
                        )}
                        {stats.totalRejections > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400 font-medium">
                            <i className="fa-solid fa-xmark mr-1"></i>
                            {stats.totalRejections}
                          </span>
                        )}
                        {stats.totalBypassed > 0 && (
                          <span className="flex items-center text-gray-500 dark:text-secondary-400">
                            <i className="fa-solid fa-forward mr-1"></i>
                            {stats.totalBypassed}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <i className={`fa-solid fa-chevron-down transition-transform text-sm ${
                      isExpanded ? 'rotate-180' : ''
                    }`}></i>
                  </div>
                </div>

                {/* Level Content */}
                {isExpanded && (
                  <div className={`px-4 space-y-3 ${isBypassedLevel ? 'pb-2' : 'pb-4'}`}>
                    {levelApprovals.length > 0 ? (
                      <>
                        {/* Active Approvals (approved, rejected, etc.) */}
                        {levelApprovals.filter(a => a.action.toLowerCase() !== 'bypassed').map((approval) => (
                          <ApprovalCard
                            key={approval.id}
                            approval={approval}
                            isExpanded={expandedApprovals[approval.id] !== false}
                            onToggle={() => toggleApproval(approval.id)}
                            getApprovalIcon={getApprovalIcon}
                            getActionBadgeVariant={getActionBadgeVariant}
                            getDecisionBadgeVariant={getDecisionBadgeVariant}
                            formatResponseTime={formatResponseTime}
                            isNotReached={isTerminalOrder && approval.action === 'pending'}
                          />
                        ))}
                        
                        {/* Bypassed Approvers Section */}
                        {levelApprovals.filter(a => a.action.toLowerCase() === 'bypassed').length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-secondary-700">
                            <p className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase mb-2">
                              Bypassed Approvers
                            </p>
                            <div className="space-y-2">
                              {levelApprovals.filter(a => a.action.toLowerCase() === 'bypassed').map((approval) => (
                                <ApprovalCard
                                  key={approval.id}
                                  approval={approval}
                                  isExpanded={false}
                                  onToggle={() => {}}
                                  getApprovalIcon={getApprovalIcon}
                                  getActionBadgeVariant={getActionBadgeVariant}
                                  getDecisionBadgeVariant={getDecisionBadgeVariant}
                                  formatResponseTime={formatResponseTime}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-6 text-gray-500 dark:text-secondary-400">
                        {isPendingLevel ? (
                          <div>
                            <i className="fa-solid fa-clock text-3xl mb-2 text-blue-400"></i>
                            <p className="text-sm">Awaiting approval from:</p>
                            <div className="mt-2 space-y-1">
                              {orderDetail.pendingApprovers.map((approver, idx) => (
                                <p key={idx} className="text-xs font-mono">{approver}</p>
                              ))}
                            </div>
                          </div>
                        ) : isBypassedLevel ? (
                          <div className="flex items-center space-x-2 text-gray-400 dark:text-secondary-500 py-1">
                            <i className="fa-solid fa-forward text-sm"></i>
                            <p className="text-xs">No approver was assigned to this step</p>
                          </div>
                        ) : (
                          <p className="text-sm">No approvals recorded yet</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Special Status Messages */}
        {orderDetail.status === 'NeedsResolved' && (
          <div className="bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex items-start">
              <i className="fa-solid fa-exclamation-triangle text-yellow-600 mt-1 mr-3"></i>
              <div>
                <h4 className="text-sm font-semibold text-yellow-800">No Valid Approvers Found</h4>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                  This order cannot proceed because no users have all the required match identifiers to approve it.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// Separate component for approval card to keep the code organized
interface ApprovalCardProps {
  approval: ApprovalHistoryDto;
  isExpanded: boolean;
  onToggle: () => void;
  getApprovalIcon: (action: string) => React.ReactNode;
  getActionBadgeVariant: (action: string) => any;
  getDecisionBadgeVariant: (decision: string) => any;
  formatResponseTime: (responseTime?: string) => string;
  isNotReached?: boolean;
}

function ApprovalCard({
  approval,
  isExpanded,
  onToggle,
  getApprovalIcon,
  getActionBadgeVariant,
  getDecisionBadgeVariant,
  formatResponseTime,
  isNotReached = false
}: ApprovalCardProps) {
  const hasLineItemActions = approval.lineItemActions && approval.lineItemActions.length > 0;
  const shouldShowComments = approval.comments && 
    !approval.comments.match(/^(Approved|Rejected|approved|rejected)\s+\d+\s+(item|items)/i) &&
    approval.comments !== "Historical approval (no detailed event data available)";

  return (
    <div className="border border-gray-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-800">
      {/* Approval Header */}
      <div
        className={`flex items-start justify-between p-3 ${hasLineItemActions ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-secondary-700' : ''}`}
        onClick={hasLineItemActions ? onToggle : undefined}
      >
        <div className="flex items-start space-x-3 flex-1">
          <div className="flex-shrink-0 mt-0.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isNotReached ? 'bg-gray-100 dark:bg-secondary-700' :
              approval.action === 'approve' ? 'bg-green-100 dark:bg-green-900' :
              approval.action === 'reject' ? 'bg-red-100 dark:bg-red-900' :
              approval.action === 'bypassed' ? 'bg-gray-100 dark:bg-secondary-700' :
              'bg-blue-100 dark:bg-blue-900'
            }`}>
              {isNotReached
                ? <i className="fa-solid fa-minus text-gray-400 dark:text-secondary-500"></i>
                : getApprovalIcon(approval.action)}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <p className={`font-medium text-sm ${isNotReached || approval.action === 'bypassed' ? 'text-gray-500 dark:text-secondary-400' : ''}`}>
                {approval.approverName}
              </p>
              {isNotReached ? (
                <span className="text-xs text-gray-400 dark:text-secondary-500 italic">Not reached</span>
              ) : (
                <>
                  <Badge variant={getActionBadgeVariant(approval.action)} size="sm">
                    {approval.action}
                  </Badge>
                  {approval.action === 'bypassed' && (
                    <span className="text-xs text-gray-500 dark:text-secondary-400 italic">(did not need to approve)</span>
                  )}
                </>
              )}
            </div>
            
            <p className="text-xs text-gray-600 dark:text-secondary-400 truncate">{approval.approverEmail}</p>
            
            {!isNotReached && approval.delegatedFromUserName && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                <i className="fa-solid fa-user-group mr-1"></i>
                On behalf of {approval.delegatedFromUserName}
              </p>
            )}
            
            {!isNotReached && approval.status !== 'pending' && approval.status !== 'bypassed' && (
              <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-secondary-400">
                <i className="fa-solid fa-calendar mr-1"></i>
                {formatDateTime(approval.actionDate)}
              </div>
            )}

            {!isNotReached && shouldShowComments && (
              <div className="mt-2 text-xs text-gray-700 dark:text-secondary-200 bg-gray-50 dark:bg-secondary-900 rounded p-2">
                <i className="fa-solid fa-comment mr-1"></i>
                {approval.comments}
              </div>
            )}
          </div>
        </div>

        {hasLineItemActions && (
          <div className="flex items-center space-x-2 ml-3">
            <span className="text-xs text-gray-600 dark:text-secondary-400">
              {approval.lineItemActions?.length || 0} {approval.lineItemActions?.length === 1 ? 'item' : 'items'}
            </span>
            <i className={`fa-solid fa-chevron-down transition-transform text-xs text-gray-400 dark:text-secondary-500 ${
              isExpanded ? 'rotate-180' : ''
            }`}></i>
          </div>
        )}
      </div>

      {/* Line Item Actions */}
      {hasLineItemActions && isExpanded && (
        <div className="border-t border-gray-200 dark:border-secondary-700 px-3 py-2 bg-gray-50 dark:bg-secondary-900">
          <p className="text-xs font-semibold text-gray-700 dark:text-secondary-200 mb-2">Line Item Actions:</p>
          <div className="space-y-2">
            {approval.lineItemActions!.map((lineItem, idx) => (
              <LineItemAction
                key={idx}
                lineItem={lineItem}
                getDecisionBadgeVariant={getDecisionBadgeVariant}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Component for individual line item action
interface LineItemActionProps {
  lineItem: ApprovalHistoryLineItemDto;
  getDecisionBadgeVariant: (decision: string) => any;
}

function LineItemAction({ lineItem, getDecisionBadgeVariant }: LineItemActionProps) {
  const isModified = lineItem.decision === 'modify' || 
    (lineItem.approvedQuantity != null && lineItem.approvedQuantity !== lineItem.originalQuantity);

  return (
    <div className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded p-2">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <p className="text-xs font-medium truncate">{lineItem.lineItemName}</p>
            <Badge variant={getDecisionBadgeVariant(lineItem.decision)} size="sm">
              {lineItem.decision}
            </Badge>
          </div>
          
          {isModified && (
            <div className="flex items-center space-x-2 text-xs text-blue-700 mt-1">
              <i className="fa-solid fa-pen-to-square"></i>
              <span>
                Quantity: {lineItem.originalQuantity} → {lineItem.approvedQuantity}
              </span>
            </div>
          )}

          {lineItem.comments && (
            <p className="text-xs text-gray-600 dark:text-secondary-400 mt-1 italic">
              "{lineItem.comments}"
            </p>
          )}

          {lineItem.modificationReason && (
            <p className="text-xs text-gray-600 dark:text-secondary-400 mt-1">
              <i className="fa-solid fa-info-circle mr-1"></i>
              {lineItem.modificationReason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
